/**
 * Blood Pressure Records CSV Export Utility
 * Generates RFC 4180 compliant CSV files with UTF-8 BOM (\uFEFF)
 * for seamless compatibility with Microsoft Excel and CJK text viewers.
 */

import { BloodPressureRecord } from '@/types/record';
import { classifyBP } from './bpClassification';
import { calculateMAP, calculatePulsePressure } from './calculations';
import { getTodayDateString } from './dateUtils';

/**
 * Standard CSV Column Definitions
 */
export const CSV_COLUMNS = [
  '記錄編號',
  '測量日期',
  '測量時間',
  '收縮壓 (mmHg)',
  '舒張壓 (mmHg)',
  '心跳 (bpm)',
  '脈壓差 (PP)',
  '平均動脈壓 (MAP)',
  'AHA血壓等級',
  '測量部位',
  '備註',
] as const;

/**
 * Escape a cell value according to RFC 4180
 * 1. If the value is null or undefined, return empty string or quote.
 * 2. If the value contains commas, quotes, CRLF or LF, enclose in double quotes.
 * 3. Double quotes inside the value are escaped by preceding with another double quote ("").
 */
export function escapeCSVCell(value: unknown): string {
  if (value === null || value === undefined) {
    return '""';
  }
  const str = String(value);
  if (
    str.includes(',') ||
    str.includes('"') ||
    str.includes('\n') ||
    str.includes('\r')
  ) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return `"${str}"`;
}

/**
 * Convert an array of BloodPressureRecord objects into an RFC 4180 CSV string
 * with UTF-8 BOM prefix (\uFEFF).
 */
export function generateCSVString(records: BloodPressureRecord[]): string {
  const headerRow = CSV_COLUMNS.map(escapeCSVCell).join(',');
  const rows: string[] = [headerRow];

  for (const record of records) {
    const stage = classifyBP(record.systolic, record.diastolic);
    const pp = calculatePulsePressure(record.systolic, record.diastolic);
    const map = calculateMAP(record.systolic, record.diastolic);

    let armText = '未註記';
    if (record.arm === 'left') armText = '左手';
    if (record.arm === 'right') armText = '右手';

    const stageLabel = `${stage.labelZh} (${stage.labelEn})`;

    const rowValues = [
      record.id,
      record.measured_date,
      record.measured_time,
      record.systolic,
      record.diastolic,
      record.pulse,
      pp,
      map,
      stageLabel,
      armText,
      record.notes || '',
    ];

    rows.push(rowValues.map(escapeCSVCell).join(','));
  }

  // Prefix with UTF-8 BOM (\uFEFF) and use CRLF (\r\n) as standard row separator
  return '\uFEFF' + rows.join('\r\n');
}

/**
 * Create a downloadable Blob from an array of blood pressure records
 */
export function createCSVBlob(records: BloodPressureRecord[]): Blob {
  const csvContent = generateCSVString(records);
  return new Blob([csvContent], {
    type: 'text/csv;charset=utf-8;',
  });
}

/**
 * Trigger immediate browser download of the CSV file
 * @param records Array of records to export
 * @param customFilename Optional filename (defaults to blood_pressure_records_YYYYMMDD.csv)
 */
export function downloadCSV(
  records: BloodPressureRecord[],
  customFilename?: string
): void {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return;
  }

  const todayStr = getTodayDateString().replace(/-/g, '');
  const filename = customFilename || `blood_pressure_records_${todayStr}.csv`;

  const blob = createCSVBlob(records);
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');

  link.href = url;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();

  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
}
